// src/components/PdfWhiteboard/Toolbar.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TOOLS, POINT_MARKERS } from '../constants/drawingTools';
import '../Replan.css';
// use SVGR import (supported by CRA/Vite)
import { ReactComponent as DrawPolygonIcon } from '../../../svg/polygon-pt-svgrepo-com.svg';
import { ReactComponent as DrawPolylineIcon } from '../../../svg/polyline-pt-svgrepo-com.svg';
import { ReactComponent as SelectRectangle } from '../../../svg/select-rectangle-svgrepo-com.svg';
import { ReactComponent as StopWarning } from '../../../svg/stop-svgrepo-com.svg';
import { ReactComponent as Highlighter } from '../../../svg/highlight-svgrepo-com.svg';
import { ReactComponent as Select } from '../../../svg/cursor-svgrepo-com.min.svg';
import { ReactComponent as ArrowText } from '../../../svg/arrows-expand-down-right-svgrepo-com.svg';
import { ReactComponent as Cotation } from '../../../svg/arrows-shrink-h-svgrepo-com.svg';

import { ReactComponent as Lock } from '../../../svg/lock-svgrepo-com.svg';
import { ReactComponent as Unlock } from '../../../svg/unlock-svgrepo-com.svg';
import ColorPicker from './ColorPicker';

// Helper to generate *only* the dynamic styles based on selection
const getDynamicButtonStyle = (tool, selectedTool) => ({
  border:
    selectedTool === tool
      ? '1px solid var(--pico-form-element-valid-active-border-color)'
      : '',
  backgroundColor:
    selectedTool === tool
      ? 'var(--pico-mark-background-color)'
      : null,
  scale: selectedTool === tool ? '1.05' : '1',
  fill: 'red',
});

// ─── Inline SVG icons for each point marker type ────────────────────────────
const MARKER_ICONS = {
  disk: (
    <svg viewBox="0 0 20 20" width="14" height="14">
      <circle cx="10" cy="10" r="6" fill="currentColor" />
    </svg>
  ),
  square: (
    <svg viewBox="0 0 20 20" width="14" height="14">
      <rect x="3" y="3" width="14" height="14" fill="currentColor" />
    </svg>
  ),
  triangle: (
    <svg viewBox="0 0 20 20" width="14" height="14">
      <polygon points="10,2 19,18 1,18" fill="currentColor" />
    </svg>
  ),
  diamond: (
    <svg viewBox="0 0 20 20" width="14" height="14">
      <polygon points="10,1 19,10 10,19 1,10" fill="currentColor" />
    </svg>
  ),
  cross: (
    <svg viewBox="0 0 20 20" width="14" height="14">
      <line x1="10" y1="1" x2="10" y2="19" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <line x1="1" y1="10" x2="19" y2="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  ),
  star: (
    <svg viewBox="0 0 20 20" width="14" height="14">
      <polygon
        points="10,1 12.9,7.6 20,8.2 14.6,13 16.2,20 10,16.3 3.8,20 5.4,13 0,8.2 7.1,7.6"
        fill="currentColor"
      />
    </svg>
  ),
};

// ─── Render the annotation tool icon using original SVG components ─────
const renderAnnotationIcon = (key) => {
  if (key === TOOLS.TEXT) return <span className="tool-icon-text">T</span>;
  const Icon = key === TOOLS.RECTANGLE ? SelectRectangle : ArrowText;
  return <Icon className="tool-icon" />;
};

const Toolbar = ({
  selectedTool,
  onSelectTool,
  strokeColor,
  onColorChange,
  strokeWidth,
  onStrokeWidthChange,
  isOrthogonalModeActive,
  setIsOrthogonalModeActive,
  isRectangleMode,
  setIsRectangleMode,
  isDraggingMode,
  setIsDraggingMode,
  isSnappingEnabled,
  setIsSnappingEnabled,
  isGridVisible,
  setIsGridVisible,
  onCreateMeasurementSummary,
  // Point marker props
  selectedPointMarker,
  onSelectPointMarker,
  // Annotation tool group props
  selectedAnnotationTool,
  onSelectAnnotationTool,
  onBlockScale,
  canUnblock,
}) => {
  const { t } = useTranslation();

  const annotationToolsLabels = {
    [TOOLS.RECTANGLE]: { label: t('replan.toolbar.rectangle'), desc: t('replan.toolbar.rectangle_desc'), shortcut: 'N' },
    [TOOLS.TEXT]: { label: t('replan.toolbar.text_label'), desc: t('replan.toolbar.text_label_desc'), shortcut: 'T' },
    [TOOLS.ARROW]: { label: t('replan.toolbar.arrow'), desc: t('replan.toolbar.arrow_desc'), shortcut: 'W' },
  };

  const isAnnotationActive = [TOOLS.TEXT, TOOLS.RECTANGLE, TOOLS.ARROW].includes(selectedTool);

  return (
    <div className="toolbar">
      {/* Tool Buttons */}
      <button
        onClick={() => onSelectTool(null)}
        data-tt={`${t('replan.toolbar.stop_desc')}\n${t('replan.toolbar.stop')}\n${t('common.shortcut', { key: 'Q', defaultValue: 'Q' })}`}
      >
        <StopWarning className="tool-icon" />
      </button>
      <button
        style={getDynamicButtonStyle(TOOLS.SELECT, selectedTool)}
        onClick={() => onSelectTool(TOOLS.SELECT)}
        data-tt={`${t('replan.toolbar.select_pan_desc')}\n${t('replan.toolbar.select_pan')}\n${t('common.shortcut', { key: 'Space', defaultValue: 'Space' })}`}
      >
        <Select className="tool-icon-select" />
      </button>

      {/* Visual Separator */}
      <div className="horizontal-separator"></div>

      <button
        style={getDynamicButtonStyle(TOOLS.PEN, selectedTool)}
        onClick={() => onSelectTool(TOOLS.PEN)}
        data-tt={`${t('replan.toolbar.pen_desc')}\n${t('replan.toolbar.pen')}\n${t('common.shortcut', { key: 'H', defaultValue: 'H' })}`}
      >
        <Highlighter className="tool-icon" />
      </button>
      {/* ─── Annotation tool group (Rectangle, Text, Arrow) ──────────────── */}
      <div className="annotation-tool-picker">
        <button
          className="annotation-tool-main-btn"
          style={{
            border: isAnnotationActive ? '1px solid var(--pico-form-element-valid-active-border-color)' : '',
            backgroundColor: isAnnotationActive ? 'var(--pico-mark-background-color)' : null,
          }}
          onClick={() => onSelectTool(selectedAnnotationTool)}
          data-tt={`${annotationToolsLabels[selectedAnnotationTool].desc}\n${annotationToolsLabels[selectedAnnotationTool].name}\n${t('common.shortcut', { key: annotationToolsLabels[selectedAnnotationTool].shortcut, defaultValue: annotationToolsLabels[selectedAnnotationTool].shortcut })}`}
        >
          <span className="annotation-tool-active-icon">
            {renderAnnotationIcon(selectedAnnotationTool)}
          </span>
        </button>

        <div className="annotation-tool-submenu">
          {Object.keys(annotationToolsLabels).map((key) => (
            <button
              key={key}
              className={`annotation-tool-option${selectedAnnotationTool === key ? ' active' : ''}`}
              data-tt={`${annotationToolsLabels[key].desc}\n${annotationToolsLabels[key].name}\n${t('common.shortcut', { key: annotationToolsLabels[key].shortcut, defaultValue: annotationToolsLabels[key].shortcut })}`}
              onClick={() => {
                onSelectAnnotationTool(key);
                onSelectTool(key);
              }}
            >
              <span className="annotation-tool-option-icon">
                {renderAnnotationIcon(key)}
              </span>
            </button>
          ))}
        </div>
      </div>

      <button
        style={getDynamicButtonStyle(TOOLS.COTATION, selectedTool)}
        onClick={() => onSelectTool(TOOLS.COTATION)}
        data-tt={`${t('replan.toolbar.cotation_desc')}\n${t('replan.toolbar.cotation')}\n${t('common.shortcut', { key: 'K', defaultValue: 'K' })}`}
      >
        <Cotation className="tool-icon" />
      </button>

      {/* ─── POINT tool with marker picker submenu ─────────────────────── */}
      <div className="point-marker-picker">
        {/* Main button: click selects the POINT tool using the current marker */}
        <button
          style={getDynamicButtonStyle(TOOLS.POINT, selectedTool)}
          onClick={() => onSelectTool(TOOLS.POINT)}
          data-tt={`${t('replan.toolbar.add_point_desc')}\n${t('replan.toolbar.add_point')}\n${t('common.shortcut', { key: 'P', defaultValue: 'P' })}`}
          className="point-marker-main-btn"
        >
          <span className="point-marker-active-icon">
            {MARKER_ICONS[selectedPointMarker] || MARKER_ICONS.disk}
          </span>
        </button>

        {/* Submenu — revealed on hover via CSS */}
        <div className="point-marker-submenu">
          {Object.entries(POINT_MARKERS).map(([key, { label }]) => (
            <button
              key={key}
              className={`point-marker-option${selectedPointMarker === key ? ' active' : ''}`}
              data-tt={`${t('replan.toolbar.point_marker_desc')}\n${label}\n${t('common.click_activate', { defaultValue: 'Activate' })}`}
              onClick={() => {
                onSelectPointMarker(key);
                onSelectTool(TOOLS.POINT);
              }}
            >
              {MARKER_ICONS[key]}
            </button>
          ))}
        </div>
      </div>

      <button
        style={getDynamicButtonStyle(TOOLS.POLYLINE, selectedTool)}
        onClick={() => onSelectTool(TOOLS.POLYLINE)}
        data-tt={`${t('replan.toolbar.polyline_desc')}\n${t('replan.toolbar.polyline')}\n${t('common.shortcut', { key: 'L', defaultValue: 'L' })}`}
      >
        <DrawPolylineIcon className="tool-icon" />
      </button>
      <button
        style={getDynamicButtonStyle(TOOLS.POLYGON, selectedTool)}
        onClick={() => onSelectTool(TOOLS.POLYGON)}
        data-tt={`${t('replan.toolbar.polygon_desc')}\n${t('replan.toolbar.polygon')}\n${t('common.shortcut', { key: 'S', defaultValue: 'S' })}`}
      >
        <DrawPolygonIcon className="tool-icon" />
      </button>

      {/* Visual Separator */}
      <div className="horizontal-separator"></div>

      {/* Measurement Summary Button */}
      <button
        onClick={onCreateMeasurementSummary}
        data-tt={`${t('replan.toolbar.create_summary_desc')}\n${t('replan.toolbar.create_summary')}\n${t('common.shortcut', { key: 'U', defaultValue: 'U' })}`}
        className="toolbar-button"
        style={{
          backgroundColor: '#10b981',
          color: 'white',
          fontWeight: 'bold',

        }}
      >
        Σ
      </button>

      {/* Block/Unblock Scale Button */}
      <button
        onClick={onBlockScale}
        data-tt={`${t('replan.toolbar.toggle_block_scale_desc')}\n${t('replan.toolbar.toggle_block_scale')}`}
        className="toolbar-button"

      >
        {canUnblock ? (
          <Unlock className="tool-icon" />
        ) : (
          <Lock className="tool-icon" />
        )}
      </button>
      {/* Visual Separator */}
      <div className="horizontal-separator"></div>
      <input
        type="checkbox"
        className="orthogonal-mode-checkbox"
        data-tt={`${t('replan.toolbar.orthogonal_mode_desc')}\n${t('replan.toolbar.orthogonal_mode')}\n${t('common.shortcut', { key: 'O', defaultValue: 'O' })}`}
        checked={isOrthogonalModeActive}
        onChange={() => {
          const newVal = !isOrthogonalModeActive;
          setIsOrthogonalModeActive(newVal);
          if (newVal) {
            setIsRectangleMode(false);
          }
        }}
      />

      <input
        type="checkbox"
        className="rectangle-mode-checkbox"
        data-tt={`${t('replan.toolbar.rectangle_mode_desc')}\n${t('replan.toolbar.rectangle_mode')}\n${t('common.shortcut', { key: 'R', defaultValue: 'R' })}`}
        checked={isRectangleMode}
        onChange={(e) => {
          const newVal = e.target.checked;
          setIsRectangleMode(newVal);
          if (newVal) {
            setIsOrthogonalModeActive(false);
          }
        }}
      />

      <input
        type="checkbox"
        className="snapping-mode-checkbox"
        data-tt={`${t('replan.toolbar.snapping_mode_desc')}\n${t('replan.toolbar.snapping_mode')}\n${t('common.shortcut', { key: 'X', defaultValue: 'X' })}`}
        checked={isSnappingEnabled}
        onChange={(e) => setIsSnappingEnabled(e.target.checked)}
      />
      <input
        type="checkbox"
        className="grid-mode-checkbox"
        data-tt={`${t('replan.toolbar.grid_mode_desc')}\n${t('replan.toolbar.grid_mode')}\n${t('common.shortcut', { key: 'G', defaultValue: 'G' })}`}
        checked={isGridVisible}
        onChange={(e) => setIsGridVisible(e.target.checked)}
      />

      {/* Color Picker */}
      <ColorPicker
        value={strokeColor}
        onChange={onColorChange}
        data-tt={`${t('replan.toolbar.stroke_color_desc')}\n${t('replan.toolbar.stroke_color')}\n${t('common.click_pick', { defaultValue: 'Pick' })}`}
      />

      {/* Stroke Width */}
      <input
        id="strokeWidthInput"
        type="number"
        min="1"
        max="50"
        step="1"
        value={strokeWidth}
        onChange={(e) => onStrokeWidthChange(Number(e.target.value))}
        data-tt={`${t('replan.toolbar.stroke_width_desc')}\n${t('replan.toolbar.stroke_width')}\n${t('common.shortcut', { key: '- / +', defaultValue: '- / +' })}`}
      />
    </div>
  );
};

export default Toolbar;
